"use strict";

// Copyright 2011-2017 The MathWorks, Inc.

define(["dojo/_base/lang", "dojo/_base/window", "mw-utils/deprecation"], function (lang, dojoWindow, deprecation) {
    return {
        generateUuid: function generateUuid() {
            // A real UUID (version 4) is rather long. Until we have need of the real thing, here is
            // a shorter random string:
            // For a real UUID use the following: 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
            return 'xxxxxxxx'.replace(/[xy]/g, function (c) {
                /*jslint bitwise: false */
                var r = Math.random() * 16 | 0,
                    v = c === 'x' ? r : r & 0x3 | 0x8;
                /*jslint bitwise: true */
                return v.toString(16);
            }).toUpperCase();
        },

        isImageData: function isImageData(obj) {
            // Firefox does not have an ImageData class for data returned by getImageData
            return !!obj && (dojoWindow.global.ImageData && obj instanceof dojoWindow.global.ImageData || obj.hasOwnProperty("width") && obj.hasOwnProperty("height") && obj.hasOwnProperty("data"));
        },

        /**
         * Checks if an object is an array without making use of instanceof. instanceof can yield
         * unexpected results when used with multiple contexts, e.g. passing objects between
         * iframes.
         *
         * @deprecated Consider using Array.isArray instead.
         * @param obj the object to check
         * @returns {boolean} true if obj is an array, false if not
         */
        isArray: function isArray(obj) {
            deprecation.deprecated({
                deprecated: {
                    module: "MW/utils/Utils",
                    method: "isArray"
                },
                geckNumber: 1444368
            });

            return Array.isArray(obj);
        },

        /**
         * Get the number of non-undefined array elements
         * @param arr
         */
        getArrayLength: function getArrayLength(arr) {
            var length = 0;
            if (!lang.isArray(arr)) {
                return false;
            }
            arr.forEach(function (item) {
                if (item) {
                    length += 1;
                }
            });
            return length;
        },

        /**
         * Checks if the given text has only whitespace characters.
         * LINE_FEED, CARRIAGE_RETURN, TAB are treated as whitespace characters.
         * Empty string is treated as non whitespace.
         *
         * @param text string of characters
         * @return {boolean}
         */
        isWhitespace: function isWhitespace(text) {
            var regex = /^\s+$/;
            return regex.test(text);
        },

        /**
         * Removes leading and trailing new line characters from string
         */
        removeSurroundingNewLines: function removeSurroundingNewLines(text) {
            while (text.charAt(0) === '\n') {
                text = text.substring(1);
            }
            while (text.charAt(text.length - 1) === '\n') {
                text = text.substring(0, text.length - 1);
            }
            return text;
        },

        /**
         * Finds first element of arr2 that is not an element of arr1
         *
         * @param array1 - Array to check elements against
         * @param array2 - Array to find unique element within
         * @returns {*} - First unique element of array2, null if no unique elements
         */
        getFirstUniqueElement: function getFirstUniqueElement(array1, array2) {
            var unique = null;

            array2.some(function (elem) {
                if (array1.indexOf(elem) === -1) {
                    unique = elem;
                    return true;
                }
            });

            return unique;
        },

        /**
         * Finds the union of the two sets of string words
         * (either as Arrays or strings separated by spaces) and
         * returns a set of unique words
         * @param set1 String |  Array of Strings
         *        e.g. "word1" or "word1 word2" | ["word1"] or ["word1", "word2"]
         * @param set2 String |  Array of Strings
         *        e.g. "word1" or "word1 word2" | ["word1"] or ["word1", "word2"]
         * @param asString Boolean true if want set returned as space separated String,
         *                  otherwise will return Array
         * @return Array | String unique set of words that were contained in either set
         */
        unionOfWordSets: function unionOfWordSets(set1, set2, asString) {
            set1 = this._stringToArray(set1);
            set2 = this._stringToArray(set2);

            var union = this.unionOfArrays(set1, set2);

            return asString ? union.join(" ") : union;
        },

        /**
         * Finds the intersection of the two sets of string words
         * (either as Arrays or strings separated by spaces) and
         * returns a set of unique words
         * @param set1 String |  Array of Strings
         *        e.g. "word1" or "word1 word2" | ["word1"] or ["word1", "word2"]
         * @param set2 String |  Array of Strings
         *        e.g. "word1" or "word1 word2" | ["word1"] or ["word1", "word2"]
         * @param asString Boolean true if want set returned as space separated String,
         *                  otherwise will return Array
         * @returns Array | String unique set of words that were contained in both sets
         */
        intersectionOfWordSets: function intersectionOfWordSets(set1, set2, asString) {
            set1 = this._stringToArray(set1);
            set2 = this._stringToArray(set2);

            var intersection = this.intersectionOfArrays(set1, set2);

            return asString ? intersection.join(" ") : intersection;
        },

        /**
         * Finds the difference of of the two sets of string words
         * (the complement of set2 in set1, all elements that are in set1 that are not in set2)
         * returns a set of unique words
         * @param set1 String |  Array of Strings
         *        e.g. "word1" or "word1 word2" | ["word1"] or ["word1", "word2"]
         * @param set2 String |  Array of Strings
         *        e.g. "word1" or "word1 word2" | ["word1"] or ["word1", "word2"]
         * @param asString Boolean true if want set returned as space separated String,
         *                  otherwise will return Array
         * @returns Array | String set of words that are contained in only the first set
         */
        differenceOfWordSets: function differenceOfWordSets(set1, set2, asString) {
            set1 = this._stringToArray(set1);
            set2 = this._stringToArray(set2);

            var difference = this.unique(this.differenceOfArrays(set1, set2));

            return asString ? difference.join(" ") : difference;
        },

        /**
         * Finds the union of the two Arrays
         * (elements that are in either array and duplicates are only represented once)
         * @param arr1
         * @param arr2
         * @returns {Array}
         */
        unionOfArrays: function unionOfArrays(arr1, arr2) {
            return this.unique(arr1.concat(arr2));
        },

        /**
         * Finds the intersection of the two Arrays
         * (elements that are contained in both arrays and duplicates are only represented once)
         * @param arr1
         * @param arr2
         * @returns {Array}
         */
        intersectionOfArrays: function intersectionOfArrays(arr1, arr2) {
            return this.nonunique(arr1.concat(arr2));
        },

        /**
         * Finds the difference of arr2 in arr1
         * (elements that are in arr1 that are not in arr2)
         * @param arr1
         * @param arr2
         * @returns {Array}
         */
        differenceOfArrays: function differenceOfArrays(arr1, arr2) {
            if (window.Map === undefined) {
                //g1512937 Not all clients have support for the Map API.
                //Fall back to a slower implementation.
                return this._legacyDifferenceOfArrays(arr1, arr2);
            }

            var isInBothArrays, elementInArray1, i;
            var mapOfArray2 = new Map();
            var result = [];

            //Convert arr2 into a map
            for (i = 0; i < arr2.length; i++) {
                mapOfArray2.set(arr2[i], null);
            }

            //Push elements in arr1, but not in arr2, into the result
            for (i = 0; i < arr1.length; i++) {
                elementInArray1 = arr1[i];
                isInBothArrays = mapOfArray2.has(elementInArray1);
                if (!isInBothArrays) {
                    result.push(elementInArray1);
                }
            }

            return result;
        },

        /**
         * Finds the difference of arr2 in arr1, without using the Map API
         * (elements that are in arr1 that are not in arr2)
         * 
         * @param arr1
         * @param arr2
         * @returns {Array}
         */
        _legacyDifferenceOfArrays: function _legacyDifferenceOfArrays(arr1, arr2) {
            return this.unique(arr1.filter(function (elem) {
                return arr2.indexOf(elem) === -1;
            }));
        },

        /**
         * Finds an array of unique elements
         * @param array
         * @returns {Array}
         */
        unique: function unique(array) {
            return array.filter(function (elem, idx) {
                return array.indexOf(elem) === idx;
            });
        },

        /**
         * Finds and array of non-unique elements
         * @param array
         * @returns {Array}
         */
        nonunique: function nonunique(array) {
            return this.unique(array.filter(function (elem, idx) {
                return array.indexOf(elem) !== idx;
            }));
        },

        /**
         * Performs element-wise equality check on two arrays.
         * Will always be true when === is true.
         * Can also return true even when === returns false.
         * Default element comparator is === .
         * @param {Array} array1
         * @param {Array} array2
         * @param {Function} [compareFunction] Optional comparison function that accepts two elements,
         *                                     and returns true if the elements are "equal", false if not.
         * @returns {boolean}
         */
        arrayEquals: function arrayEquals(array1, array2, compareFunction) {
            if (array1 === array2) {
                return true;
            }
            if (array1.length !== array2.length) {
                return false;
            }
            if (!compareFunction) {
                compareFunction = function compareFunction(elem1, elem2) {
                    return elem1 === elem2;
                };
            }
            for (var i = 0, len = array1.length; i < len; i++) {
                if (!compareFunction(array1[i], array2[i])) {
                    return false;
                }
            }
            // if we get here, all elements were the same
            return true;
        },

        removeEmptyElements: function removeEmptyElements(array) {
            return array.filter(function (elem) {
                return !(elem === "" || elem === null || elem === undefined);
            });
        },

        _stringToArray: function _stringToArray(str) {
            if (!this._isString(str)) {
                return str;
            }
            return this.removeEmptyElements(str.split(" "));
        },

        _isString: function _isString(str) {
            return typeof str === "string" || str instanceof String;
        }
    };
});

